import axiosInstance from './apiinterseptor';

export interface ISPOCDetails {
  name: string;
  email: string;
  phone: string;
  designation: string;
}

export interface IStatusChangeHistory {
  _id: string;
  oldStatus?: 'underProcess' | 'onboarded' | 'notOnboarded';
  newStatus: 'underProcess' | 'onboarded' | 'notOnboarded';
  remarks?: string;
  updatedBy: string;
  updatedAt: string;
}

export interface College {
  _id: string;
  name: string;
  location: string;
  contactPerson: string;
  status: 'underProcess' | 'onboarded' | 'notOnboarded';
  remarks?: string;
  referredBy?: {
    _id: string;
    username: string;
    email?: string;
    phone?: string;
    role?: string;
    ledby?: string | {
      username?: string;
      email?: string;
      phone?: string;
    };
  };
  type?: string;
  spocDetails?: ISPOCDetails[];
  statusChangeHistory?: IStatusChangeHistory[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateCollegeRequest {
  name: string;
  location: string;
  spocDetails: ISPOCDetails[];
  // optional type field (matches backend list naming)
  college_type?: string;
  type?: string;
  referredByFbdeUserId?: string;
}

export interface UpdateCollegeRequest {
  name?: string;
  location?: string;
  contactPerson?: string;
  email?: string;
  phone?: string;
  status?: 'underProcess' | 'onboarded' | 'notOnboarded';
  spocDetails?: ISPOCDetails[];
}

export interface UpdateCollegeStatusRequest {
  status: 'underProcess' | 'onboarded' | 'notOnboarded';
  remarks?: string;
}

export interface CollegeListResponse {
  colleges: College[];
  totalResults: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface CollegeFilters {
  page?: number;
  limit?: number;
  search?: string;
  status?: 'underProcess' | 'onboarded' | 'notOnboarded';
  dateFrom?: string;
  dateTo?: string;
  // optional type to filter list - 'College' or 'University'
  type?: 'College' | 'University' | 'Institute';
}

export interface CollegeListItem {
  university: string;
  college: string;
  college_type: string;
  state: string;
  status?: 'underProcess' | 'onboarded' | 'notOnboarded';
  district?: string;
}

/**
 * Get colleges with filters and pagination
 */
export const getColleges = async (filters: CollegeFilters = {}): Promise<CollegeListResponse> => {
  try {
    const queryParams = new URLSearchParams();
    
    if (filters.page !== undefined) queryParams.append('page', filters.page.toString());
    if (filters.limit !== undefined) queryParams.append('limit', filters.limit.toString());
    if (filters.search) queryParams.append('search', filters.search);
    if (filters.status) queryParams.append('status', filters.status);
    if (filters.dateFrom) queryParams.append('dateFrom', filters.dateFrom);
    if (filters.dateTo) queryParams.append('dateTo', filters.dateTo);
    if (filters.type) queryParams.append('type', filters.type);

    const response = await axiosInstance.get(`/colleges?${queryParams.toString()}`);
    return response.data.data;
  } catch (error: any) {
    console.error('Error fetching colleges:', error.response?.data?.message);
    throw error;
  }
};

/**
 * Get a college by ID
 */
export const getCollegeById = async (id: string): Promise<College> => {
  try {
    const response = await axiosInstance.get(`/colleges/${id}`);
    return response.data.data;
  } catch (error: any) {
    console.error('Error fetching college:', error.response?.data?.message);
    throw error;
  }
};

/**
 * Create a new college
 */
export const createCollege = async (collegeData: CreateCollegeRequest): Promise<College> => {
  try {
    const response = await axiosInstance.post('/colleges', collegeData);
    return response.data.data;
  } catch (error: any) {
    console.error('Error creating college:', error.response?.data?.message);
    throw error;
  }
};

/**
 * Update a college
 */
export const updateCollege = async (id: string, collegeData: UpdateCollegeRequest): Promise<College> => {
  try {
    const response = await axiosInstance.put(`/colleges/${id}`, collegeData);
    return response.data.data;
  } catch (error: any) {
    console.error('Error updating college:', error.response?.data?.message);
    throw error;
  }
};

/**
 * Delete a college
 */
export const deleteCollege = async (id: string): Promise<void> => {
  try {
    await axiosInstance.delete(`/colleges/${id}`);
  } catch (error: any) {
    console.error('Error deleting college:', error.response?.data?.message);
    throw error;
  }
};

/**
 * Update college status
 */
export const updateCollegeStatus = async (id: string, statusData: UpdateCollegeStatusRequest): Promise<College> => {
  try {
    const response = await axiosInstance.patch(`/colleges/${id}/status`, statusData);
    return response.data.data;
  } catch (error: any) {
    console.error('Error updating college status:', error.response?.data?.message);
    throw error;
  }
};

/**
 * Search colleges from college list
 */
export const searchCollegeList = async (searchTerm: string,type: "College" | "University" | "Institute"): Promise<CollegeListItem[]> => {
  try {
    console.log('API: Searching colleges with term:', searchTerm);
    const queryParams = new URLSearchParams();
    if (searchTerm) queryParams.append('search', searchTerm);
    if (type) queryParams.append('type', type);
    const url = `/colleges/college-list?${queryParams.toString()}`;
    console.log('API: Making request to:', url);
    
    const response = await axiosInstance.get(url);
    console.log('API: Response received:', response.data);
    return response.data.data;
  } catch (error: any) {
    console.error('API: Error searching college list:', error.response?.data?.message || error.message);
    throw error;
  }
};
